import type {
  MappingProperty,
  SearchHit,
  SearchProfile,
} from '@elastic/elasticsearch/lib/api/types'
import type { SearchTypeFromMappingType } from '../mapping'

export type SearchResponse<T> = {
  nbPages: number
  page: number
  hits: T[]
  profile?: SearchProfile
}

export type SerializedSearchObjectMetadata = {
  objectID: string
  title: string
  type: 'rich_text' | 'discussion' | 'collection'
  noteId: string
  channelId: string
  createdBy: string
  createdAt: Date
  updatedAt: Date
  archivedAt: Date | null
  iconShape: string | null
  iconVariant: string | null
  iconColor: string | null
  isArchived: boolean
  isTemplate: boolean
  organizationId: string
  _tags: string[]
  restrictedTo: string[]
  parentNotesIdList: string[]
  parentNotesTitleList: string[]
  fullPath: string
  isChannelDoc: boolean
  hasChildren: boolean
  coverImageUrl: string | null | undefined
  isCollectionRoot: boolean
  writerIds: string[]
  channelType: 'public' | 'private' | 'personal' | 'public_read_only'
  channelPurpose: 'regular' | 'templates' | 'discussions' | 'external_sources'
  isPubliclyShared: boolean
  reviewState:
    | null
    | 'none'
    | 'pending_review'
    | 'final'
    | 'out_of_date'
    | 'verification_requested'
  lastEditedAt: null | Date
  lastEditedBy: string[]
  ownerId: string
  externalType: string | null
  externalUrl: string | null
}

export type SerializedUserSearchObject = {
  objectID: string
  displayName: string
  username: string
  email: string
  photoURL: null | string
  organizationRole: string
  organizationId: string
  updatedAt: string
  createdAt: string
  lastActivityAt: null | string
  isArchived: boolean
  invitePending: null | boolean
  _tags: string[]
  description: string
}

export type SerializedUserGroupSearchObject = {
  objectID: string
  name: string
  description: string
  _tags: string[]
  organizationId: string
  memberIdList: string[]
}

export enum AskXAutomationDestinationType {
  EMAIL = 'email',
  SLACK = 'slack',
}

export type GenerateAskXAutomationInput = {
  audience: string[]
  userId: string
  organizationId: string
  parentIdList?: string[]
  sourceTypeList?: string[]
  updatedAfter?: string
  updatedBefore?: string
  customPrompt?: string
  contextPrompt?: string
  destinationType: AskXAutomationDestinationType
}

export type GenerateAskXAutomationOutput = {
  error?: string
  markdown?: string
}

export type GenerateAskXAutomationTestingOutput = {
  error?: string
  markdown?: string
  emailSubject?: string
  oldestTestingHit?: string
  oldestRealHit?: string
  tooManyHits: boolean
  largeTokenCountTooManyHits: boolean
}

export enum GenericMappingType {
  KEYWORD = 'keyword',
  DATE = 'date',
  NUMBER = 'number',
  BOOLEAN = 'boolean',
}

export type GenericMapping = {
  key: string
  name?: string
  description?: string
  type: GenericMappingType
  enum?: readonly string[]
  enumFromCollapseEsQuery?: boolean
  enumFromTermsAggEsQuery?: boolean
  disabled?: boolean
}

export type GenericMappingTypeToValueType = {
  [GenericMappingType.KEYWORD]: string
  [GenericMappingType.DATE]: Date | string
  [GenericMappingType.NUMBER]: number
  [GenericMappingType.BOOLEAN]: boolean
}

export type GenericMappingTypeToHitType = {
  [GenericMappingType.KEYWORD]?: string[]
  [GenericMappingType.DATE]?: (Date | string)[]
  [GenericMappingType.NUMBER]?: number[]
  [GenericMappingType.BOOLEAN]?: boolean[]
}

export type TypedGenericMetadata<T extends readonly GenericMapping[]> = {
  [K in T[number]['key']]?: Array<
    | GenericMappingTypeToValueType[Extract<T[number], { key: K }>['type']]
    | undefined
  >
}

export type MetadataValue =
  | GenericMappingTypeToValueType[GenericMappingType]
  | undefined

export type SearchObjectFieldsTypeFromMappingObjectType<
  T extends Record<string, MappingProperty>
> = {
  [K in keyof T]?: K extends 'organizationId'
    ? string
    : SearchTypeFromMappingType<T[K]['type']>
} & { organizationId: string }

export type SearchResult<T> = {
  [K in keyof SearchHit<T>]: K extends 'fields'
    ? {
        [K2 in keyof T]?: T[K2][]
      }
    : K extends 'highligh'
    ? T extends string
      ? string
      : never
    : SearchHit<T>[K]
}

export type SearchErrorResponse = {
  id: string
  message: string
  status: number
}

export enum SuperAutomationDestinationType {
  EMAIL = 'email',
  SLACK = 'slack',
}

export type GenerateSuperAutomationInput = {
  audience: string[]
  userId: string
  organizationId: string
  parentIdList?: string[]
  sourceTypeList?: string[]
  updatedAfter?: string
  updatedBefore?: string
  customPrompt?: string
  contextPrompt?: string
  destinationType: SuperAutomationDestinationType
}

export type GenerateSuperAutomationOutput = {
  error?: string
  markdown?: string
}

export type GenerateSuperAutomationTestingOutput = {
  error?: string
  markdown?: string
  emailSubject?: string
  oldestTestingHit?: string
  oldestRealHit?: string
  tooManyHits: boolean
  largeTokenCountTooManyHits: boolean
}
